import { Button, NextLinkFromReactRouter, Text } from '@pancakeswap/uikit'
import { useWeb3React } from '@pancakeswap/wagmi'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { useTranslation } from '@pancakeswap/localization'
import styled from 'styled-components'
import useTheme from 'hooks/useTheme'
import { formatTokenPrice } from 'utils/formatTokenPrice'
import { palette } from '../colors'
import SwapMockCard from './SwapMockCard'
import { useQomUsdtPrice } from '../hooks/useQomPrice'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 32px 0 56px;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    align-items: center;
    gap: 56px;
    padding: 64px 0 80px;
  }
`

const Copy = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 640px;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex: 1 1 0;
    align-items: flex-start;
    text-align: left;
  }
`

const Eyebrow = styled.div<{ children?: React.ReactNode; $card: string; $border: string; $fg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: ${({ $card }) => $card};
  border: 1px solid ${({ $border }) => $border};
  color: ${({ $fg }) => $fg};
  font-size: 13px;
  font-weight: 600;
`

const Pulse = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #31d0aa;
  display: inline-block;
  box-shadow: 0 0 0 4px rgba(49, 208, 170, 0.18);
`

const Headline = styled(Text)`
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -1.2px;
  line-height: 1.05;
  word-wrap: break-word;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 52px;
    letter-spacing: -1.6px;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 56px;
    letter-spacing: -1.8px;
  }
  ${({ theme }) => theme.mediaQueries.xxl} {
    font-size: 68px;
    letter-spacing: -2.2px;
  }
`

const Subtitle = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  max-width: 540px;

  ${({ theme }) => theme.mediaQueries.lg} {
    font-size: 18px;
  }
`

const TrustRow = styled.div<{ children?: React.ReactNode; $border: string }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px 24px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid ${({ $border }) => $border};
  width: 100%;

  ${({ theme }) => theme.mediaQueries.lg} {
    justify-content: flex-start;
  }
`

const TrustItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const TrustLabel = styled.span<{ children?: React.ReactNode; $color: string }>`
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1px;
  color: ${({ $color }) => $color};
`

const TrustValue = styled.span<{ children?: React.ReactNode; $color: string }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`

const CTAButton = styled(Button)`
  background: #204080;
  color: #fff;
  border-radius: 12px;
  padding: 14px 28px;
  font-weight: 600;
  font-size: 16px;
  height: auto;
  &:hover {
    background: #2a52a0 !important;
  }
`

const HeroCardSlot = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex: 0 1 420px;
    width: auto;
  }
`

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
    align-items: center;
    width: auto;
    justify-content: center;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    justify-content: flex-start;
  }
`

const Hero: React.FC<React.PropsWithChildren> = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { theme } = useTheme()
  const p = palette(theme.isDark)
  const { price, loading } = useQomUsdtPrice()

  return (
    <Wrapper>
      <Copy>
        <Eyebrow $card={p.card} $border={p.border} $fg={p.fgSecondary}>
          <Pulse />
          {t('Live on QOM Mainnet')}
        </Eyebrow>

        <Headline as="h1">
          <span style={{ color: p.fg, display: 'block' }}>{t('QomSwap made easy')}</span>
          <span style={{ color: p.cobalt, display: 'block' }}>{t('For teh people.')}</span>
        </Headline>

        <Subtitle color={p.fgSecondary}>
          {t('The native DEX of QOM Chain (QL1). Swap, bridge and earn — directly from your wallet.')}
        </Subtitle>

        <ActionRow>
          {!account && <ConnectWalletButton />}
          <NextLinkFromReactRouter to="/swap">
            <CTAButton>{t('Trade Now')} →</CTAButton>
          </NextLinkFromReactRouter>
        </ActionRow>

        <TrustRow $border={p.border}>
          <TrustItem>
            <TrustLabel $color={p.fgSecondary}>QOM / qUSDT</TrustLabel>
            <TrustValue $color={p.fg}>{price ? formatTokenPrice(price) : '—'}</TrustValue>
          </TrustItem>
          <TrustItem>
            <TrustLabel $color={p.fgSecondary}>NETWORK</TrustLabel>
            <TrustValue $color={p.fg}>QL1 · 766</TrustValue>
          </TrustItem>
          <TrustItem>
            <TrustLabel $color={p.fgSecondary}>EXPLORER</TrustLabel>
            <TrustValue $color={p.fg}>scan.qom.one</TrustValue>
          </TrustItem>
        </TrustRow>
      </Copy>

      <HeroCardSlot>
        <SwapMockCard price={price} loading={loading} />
      </HeroCardSlot>
    </Wrapper>
  )
}

export default Hero
